import { Button, Card } from '@citizen-walk/shared-ui';
import type { Survey, SurveyResponse } from '@citizen-walk/shared-types';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import {
  aggregateResponses,
  aggregationToCsv,
  buildAggregationFilename,
  type QuestionAggregation,
} from './responseToCsv';
import {
  answerValueToString,
  buildResponseFilename,
  responsesToCsv,
  responsesToJson,
} from './surveyResponseExport';
import { fillRespondentProfiles } from './respondentProfile';
import { useSurveyPersonIds } from './useSurveyPersonIds';
import { useSurveys } from './useSurveys';
import { useSurveyResponses } from './useSurveyResponses';

/**
 * 全回答マトリクス (個票テーブル、AW8 / docs/108 High1)。
 *
 * 旧2 `old2/.../apps/admin/src/pages/SurveyResponsesPage.tsx::ResponseTable` (L287-369) を踏襲。
 * 1 行 = 1 回答者、固定列 (ニックネーム / 年齢層 / 性別 / 所属 / 区分 / 参加セッション / 提出日時)
 * + 各質問の回答セル (選択肢は保存された文言をそのまま表示、CSV と同じ表記)。横スクロール対応 (overflow-x-auto)。
 */
function ResponseTable({ survey, responses }: { survey: Survey; responses: SurveyResponse[] }) {
  const headers = [
    'ニックネーム',
    '年齢層',
    '性別',
    '所属',
    '区分',
    '参加セッション',
    '提出日時',
    ...survey.questions.map((q) => q.title || '(無題)'),
  ];

  return (
    <div className="overflow-x-auto border border-slate-200 rounded">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 sticky top-0">
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                className="px-2 py-2 text-left whitespace-nowrap border-r border-slate-200 font-semibold text-slate-700"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {responses.map((r) => {
            const snap = r.participantSnapshot;
            return (
              <tr key={r.participantUid} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="px-2 py-1 border-r border-slate-200 whitespace-nowrap">
                  {snap.nickname || '—'}
                </td>
                <td className="px-2 py-1 border-r border-slate-200 whitespace-nowrap">
                  {snap.ageGroup ?? '—'}
                </td>
                <td className="px-2 py-1 border-r border-slate-200 whitespace-nowrap">
                  {snap.gender ?? '—'}
                </td>
                <td className="px-2 py-1 border-r border-slate-200 whitespace-nowrap">
                  {snap.relationship ?? '—'}
                </td>
                <td className="px-2 py-1 border-r border-slate-200 whitespace-nowrap">
                  {snap.attribute ?? '—'}
                </td>
                <td className="px-2 py-1 border-r border-slate-200 whitespace-nowrap">
                  {snap.joinedSessions.length === 0
                    ? '—'
                    : snap.joinedSessions.map((s) => s.name).join(', ')}
                </td>
                <td className="px-2 py-1 border-r border-slate-200 whitespace-nowrap">
                  {new Date(r.updatedAt).toLocaleString('ja-JP')}
                </td>
                {survey.questions.map((q) => {
                  const cell = answerValueToString(r.answers[q.id]);
                  return (
                    <td
                      key={q.id}
                      className="px-2 py-1 border-r border-slate-200 max-w-xs truncate"
                      title={cell}
                    >
                      {cell}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Survey 回答一覧 + CSV/JSON エクスポートページ (GAP-A10、Sprint BE)。
 *
 * - 認可: master / district admin (担当地区) のみ表示。
 * - エクスポートは client-side (callable 不要、Blob → ObjectURL → <a download> でブラウザ DL)。
 * - 上限: なし (Firestore 1 query で 1000+ 件読込時は将来 pagination 検討)。
 *
 * 旧 v1 `old/apps/admin/src/pages/SurveyResponsesPage.tsx` (xlsx 出力) を v2 で CSV/JSON 2 形式に
 * 縮小 (xlsx 依存追加を回避、CLAUDE.md 制約)。
 */

/**
 * 1 質問分の集計を簡易表示 (Sprint BG-DataSettings 12)。
 * radio/dropdown/checkbox → option 別 bar、numeric → min/max/mean、text → answered 数、date/time → 上位 5 件。
 */
function AggregationRow({ agg }: { agg: QuestionAggregation }) {
  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
          {agg.type}
        </span>
        <strong className="text-sm text-slate-900">{agg.title || '(無題)'}</strong>
        <span className="text-[11px] text-slate-500">
          回答 {agg.answeredCount} / 未回答 {agg.unansweredCount}
        </span>
      </div>
      {(agg.type === 'radio' || agg.type === 'dropdown' || agg.type === 'checkbox') && (
        <ul className="grid grid-cols-1 gap-1">
          {agg.options.map((o) => {
            const denom = agg.answeredCount || 1;
            const pct = Math.round((o.count / denom) * 100);
            return (
              <li key={o.label} className="text-xs flex items-center gap-2">
                <span
                  className={`w-32 truncate ${o.notInOptions ? 'text-slate-400' : 'text-slate-700'}`}
                  title={
                    o.notInOptions
                      ? '現在の選択肢にない回答（選択肢の文言を直す前の回答など）'
                      : undefined
                  }
                >
                  {o.label}
                  {o.notInOptions && '（現在の選択肢にない）'}
                </span>
                <span className="flex-1 bg-slate-100 rounded h-3 overflow-hidden">
                  <span className="block bg-primary-500 h-full" style={{ width: `${pct}%` }} />
                </span>
                <span className="w-20 text-right text-slate-600">
                  {o.count} ({pct}%)
                </span>
              </li>
            );
          })}
          {agg.otherCount > 0 && (
            <li className="text-xs text-slate-500">その他: {agg.otherCount} 件</li>
          )}
        </ul>
      )}
      {(agg.type === 'linear_scale' || agg.type === 'rating' || agg.type === 'number') && (
        <p className="text-xs text-slate-700">
          min: {agg.min ?? '—'} / max: {agg.max ?? '—'} / mean:{' '}
          {agg.mean === null ? '—' : agg.mean.toFixed(2)}
        </p>
      )}
      {(agg.type === 'short_text' || agg.type === 'long_text') && (
        <p className="text-xs text-slate-700">テキスト回答 {agg.textCount} 件</p>
      )}
      {(agg.type === 'date' || agg.type === 'time') && (
        <ul className="grid grid-cols-1 gap-0.5">
          {agg.valueTallies.slice(0, 5).map((t) => (
            <li key={t.value} className="text-xs text-slate-700">
              {t.value}: {t.count} 件
            </li>
          ))}
          {agg.valueTallies.length > 5 && (
            <li className="text-[11px] text-slate-400">他 {agg.valueTallies.length - 5} 値…</li>
          )}
        </ul>
      )}
    </div>
  );
}

function downloadBlob(content: string, filename: string, mime: string): void {
  // CSV は UTF-8 BOM 付き (Excel 互換、U+FEFF を先頭付与)、JSON は BOM 無し。
  // Sprint BG Part 4 reviewer follow-up: 意図的に irregular whitespace (BOM) を埋める。
  const isCsv = mime.startsWith('text/csv');
  // eslint-disable-next-line no-irregular-whitespace -- BOM (U+FEFF) for Excel CSV compatibility
  const body = isCsv ? `﻿${content}` : content;
  const blob = new Blob([body], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // ObjectURL は即時 revoke (大規模 file でも browser が DL 開始済のため安全)
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function SurveyResponsesPage() {
  const { districtId, surveyId } = useParams<{ districtId: string; surveyId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const surveysState = useSurveys(districtId ?? null);
  const responsesState = useSurveyResponses(districtId ?? null, surveyId ?? null);
  // 2026-09-18: 出力に「同一人物ID」を入れる (端末を引き継いだ人も 1 人として集計できるように)。
  const {
    personIdByUid,
    unavailable: personIdUnavailable,
    participantByUid,
  } = useSurveyPersonIds(districtId ?? null);
  const sessionsState = useSessions(districtId ?? null);
  // 回答に記録されていない回答者情報 (2026-10-09 以前の回答はニックネームしか記録していない) を
  // 参加者の今の登録内容で補う。個票・CSV・JSON で同じものを使う。
  const responses = useMemo(
    () =>
      responsesState.status === 'ready'
        ? fillRespondentProfiles(
            responsesState.items,
            participantByUid,
            personIdByUid,
            sessionsState.items,
          )
        : [],
    [responsesState, participantByUid, personIdByUid, sessionsState.items],
  );

  const survey = useMemo(() => {
    if (surveysState.status !== 'ready' || !surveyId) return null;
    return surveysState.items.find((s) => s.id === surveyId) ?? null;
  }, [surveysState, surveyId]);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canView = isMaster || isDistrictAdminOfThis;

  // Low (docs/108): 参加者向け回答画面を別タブで開く link (urlToken 解決後のみ)。
  const answerFormUrl = useMemo(() => {
    if (districtState.status !== 'ready' || !surveyId) return null;
    const token = districtState.district.urlToken;
    if (!token) return null;
    return `${window.location.origin}/m/${token}/surveys/${surveyId}`;
  }, [districtState, surveyId]);

  function handleExportCsv() {
    if (!survey || responsesState.status !== 'ready') return;
    const content = responsesToCsv(survey, responses, personIdByUid);
    const filename = buildResponseFilename(survey.id, 'csv');
    downloadBlob(content, filename, 'text/csv');
  }

  function handleExportJson() {
    if (!survey || responsesState.status !== 'ready') return;
    const content = responsesToJson(survey, responses, personIdByUid);
    const filename = buildResponseFilename(survey.id, 'json');
    downloadBlob(content, filename, 'application/json');
  }

  /** Sprint BG-DataSettings 12 — 集計 CSV (質問別 / option 別 / 数値統計)。 */
  function handleExportAggregationCsv() {
    if (!survey || responsesState.status !== 'ready') return;
    const agg = aggregateResponses(survey, responsesState.items);
    const content = aggregationToCsv(agg);
    const filename = buildAggregationFilename(survey.id);
    downloadBlob(content, filename, 'text/csv');
  }

  // 集計を responses state からメモ化 (UI table 用)
  const aggregation = useMemo(() => {
    if (!survey || responsesState.status !== 'ready') return null;
    return aggregateResponses(survey, responsesState.items);
  }, [survey, responsesState]);

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}/surveys` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← アンケート一覧
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          回答一覧
          {survey ? ` — ${survey.title || '(無題)'}` : ''}
          {districtState.status === 'ready' ? ` / ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          GAP-A10 (Sprint BE) — 回答を CSV / JSON でエクスポート + 個票 (全回答マトリクス) を閲覧。
        </p>
        {answerFormUrl && (
          <a
            href={answerFormUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-block mt-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            ↗ 回答画面を別ウィンドウで開く
          </a>
        )}
      </header>

      {!canView && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区の回答閲覧権限がありません (role: {authState.role ?? 'none'}、地区: {districtId}
            )。
          </p>
        </Card>
      )}

      {canView && (
        <>
          <Card padding="md" className="mb-6 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-slate-700">
              {responsesState.status === 'ready'
                ? `回答 ${responsesState.items.length} 件`
                : responsesState.status === 'loading'
                  ? '読込中…'
                  : `読込エラー: ${responsesState.error.message}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleExportCsv}
                disabled={
                  !survey || responsesState.status !== 'ready' || responsesState.items.length === 0
                }
                title="CSV (UTF-8 BOM、Excel 互換)"
              >
                📥 CSV
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleExportJson}
                disabled={
                  !survey || responsesState.status !== 'ready' || responsesState.items.length === 0
                }
                title="JSON (question metadata 込み)"
              >
                📥 JSON
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleExportAggregationCsv}
                disabled={
                  !survey || responsesState.status !== 'ready' || responsesState.items.length === 0
                }
                title="集計 CSV (質問別 / option 別 / 数値統計)"
              >
                📊 集計 CSV
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              {personIdUnavailable
                ? '※ 参加者一覧を取得できなかったため、同一人物ID は回答者IDと同じ値になります。'
                : '※ 回答には「同一人物ID」を付けます。端末を引き継いだ人も 1 人として集計できます。'}
            </p>
          </Card>

          {/* Sprint BG-DataSettings 12 — 集計表 (回答数 / 質問別 / option 別) */}
          {aggregation && aggregation.totalResponses > 0 && survey && (
            <Card padding="md" className="mb-6">
              <h2 className="text-base font-bold text-slate-900 mb-3">
                📊 集計 ({aggregation.totalResponses} 件)
              </h2>
              <ul className="grid grid-cols-1 gap-2">
                {aggregation.questions.map((q) => (
                  <li key={q.questionId}>
                    <AggregationRow agg={q} />
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {responsesState.status === 'ready' && responsesState.items.length === 0 && (
            <Card padding="md">
              <p className="text-sm text-slate-600">まだ回答はありません。</p>
            </Card>
          )}

          {/* 個票テーブル (全回答マトリクス、AW8 / docs/108 High1) */}
          {responsesState.status === 'ready' && responsesState.items.length > 0 && survey && (
            <Card padding="md">
              <h2 className="text-base font-bold text-slate-900 mb-3">
                個票 (1 行 = 1 回答者、{responsesState.items.length} 件)
              </h2>
              <p className="text-xs text-slate-500 mb-2">
                回答時に記録されていない回答者の情報（ニックネーム・年齢層など）は、参加者の今の登録内容で表示しています。
              </p>
              <ResponseTable survey={survey} responses={responses} />
            </Card>
          )}
        </>
      )}
    </>
  );
}
